import React from "react";
import PropTypes from "prop-types";
import Dialog from "../Dialog";
import MUIDialogActions from "@mui/material/DialogActions";
import MUITypography from "@mui/material/Typography";
import MUIButton from "@mui/material/Button";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from "@mui/material/TextField";
import MUITable from "@mui/material/Table";
import MUITableBody from "@mui/material/TableBody";
import MUITableCell from "@mui/material/TableCell";
import MUITableContainer from "@mui/material/TableContainer";
import MUITableHead from "@mui/material/TableHead";
import MUITableRow from "@mui/material/TableRow";
import MUIPaper from "@mui/material/Paper";
import MUIGrid from "@mui/material/Grid2";
import MUIBox from "@mui/material/Box";
import MUITooltip from "@mui/material/Tooltip";
import MUIOpenInNewIcon from "@mui/icons-material/OpenInNew";
import MUILocalShippingIcon from "@mui/icons-material/LocalShipping";
import MUICheckIcon from "@mui/icons-material/Check";
import Progress from "../Progress";
import OrderService from "../../Seating/Security/OrderService/orderService";
import moment from "moment";
import Location from "../../Utility/Crud/Location";
import Term from "../../Utility/Crud/Term";
import MainContext from "../../Context/MainContext/MainContext";

const setFilterMatches = (filter, key, value) => {
  // We use this helper so the dialog only allows routing updates for parts
  // that are actually in-scope for the current queue.
  const values = filter?.[key]?.properties?.value || [];
  const operator =
    filter?.[key]?.properties?.operator === "not in" ? "not in" : "in";
  const included = values.includes(value);

  return operator === "not in" ? !included : included;
};

export default class DialogUpdateServiceQuotePart extends Dialog {
  static contextType = MainContext; // For legacy class components

  componentDidUpdate(prevProps) {
    if (
      prevProps.serviceQuoteParts.length !== this.props.serviceQuoteParts.length
    ) {
      this.setState({
        serviceQuoteParts: this.props.serviceQuoteParts,
        originalServiceQuoteParts: this.props.serviceQuoteParts.map((part) => ({
          id: part.id,
          rmaNumber: part.rmaNumber,
          trackingNumber: part.trackingNumber,
        })),
      });
    }
    if (prevProps.open !== this.props.open) {
      this.setState({
        updatedServiceQuoteParts: [],
      });
    }
  }

  /**
   * Render header title of dialog
   */
  renderHeader() {
    return (
      <>
        <MUITypography variant="h6" color="textPrimary">
          Receive Parts
        </MUITypography>
      </>
    );
  }

  /**
   * Render main content of dialog
   */
  renderContent() {
    if (
      this.state.serviceQuoteParts &&
      this.state.serviceQuoteParts.length > 0
    ) {
      return (
        <>
          {this.renderOrderLink()}
          {this.renderServiceQuotePartTable()}
        </>
      );
    } else {
      return <Progress message="Fetching Data..." />;
    }
  }

  /**
   * Render brightree and sales pilot links at top of the dialog
   */
  renderOrderLink() {
    const handleOnClickOpenOrder = (orderId) => {
      OrderService.getOrderActivity(orderId).then((order) => {
        let targetUrl = `/chart/${order.patientId}/order/${order.id}`;
        window.open(targetUrl, "_blank");
      });
    };

    return this.state.serviceQuoteParts &&
      this.state.serviceQuoteParts.length > 0 ? (
      <MUIGrid container spacing={3}>
        {this.state.serviceQuoteParts[0]["orderActivityId"] ? (
          <MUIGrid size="auto">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleOnClickOpenOrder(
                  this.state.serviceQuoteParts[0]["orderActivityId"],
                );
              }}
            >
              <MUITypography
                variant={"body2"}
                sx={{ display: "flex", alignItems: "center" }}
              >
                {`Order # ${this.state.serviceQuoteParts[0]["orderActivityId"]}`}
                <MUIOpenInNewIcon fontSize="small" sx={{ ml: "5px" }} />
              </MUITypography>
            </a>
          </MUIGrid>
        ) : null}
      </MUIGrid>
    ) : null;
  }

  /**
   * Render all invoice data and claim status dropdown
   */
  renderServiceQuotePartTable() {
    const getRoutingOptions = (
      serviceQuotePart,
      isFieldInteractionDisabled,
    ) => {
      const { locationId, serviceQuotePartStatusTermId } = serviceQuotePart;
      const locationName = Location.getDisplayName(
        this.context.locationsIndexed[locationId],
      );

      const statusNames = Object.values(this.context.termsIndexed)
        .filter((term) => term.type === "service_quote_part_status")
        .reduce((acc, term) => {
          acc[term.id] = term.name;
          return acc;
        }, {});

      if (isFieldInteractionDisabled) {
        return [
          {
            title: locationName,
            value: locationId,
            statusTerm: serviceQuotePartStatusTermId,
          },
        ];
      }

      switch (serviceQuotePartStatusTermId) {
        case 66: // Pending - Local Office
          return [
            {
              title: `Keep at ${locationName}`,
              value: locationId,
              statusTerm: 110, // Removed Sales Order
              complete: null,
            },
            {
              title: statusNames[71],
              value: 52,
              statusTerm: 71,
              complete: null,
            },
            {
              title: "Send to Rehab Express",
              value: 45,
              statusTerm: 67,
              complete: null,
            },
            {
              title: "Return to Manufacturer",
              value: 52,
              statusTerm: 68,
              complete: null,
            },
            {
              title: statusNames[70],
              value: 45,
              statusTerm: 70,
              complete: null,
            },
            {
              title: statusNames[69],
              value: locationId,
              statusTerm: 69,
              complete: new Date().toISOString(),
            },
          ];

        case 69: // Never Received - Tech
          return [
            {
              title: statusNames[69],
              value: locationId,
              statusTerm: 69,
              complete: new Date().toISOString(),
            },
          ];

        case 67: // Pending - Rehab Express
          return [
            {
              title: "Keep at Rehab Express",
              value: 45,
              statusTerm: 74,
              complete: new Date().toISOString(),
            },
            {
              title: "Return to Manufacturer",
              value: 52,
              statusTerm: 68,
              complete: null,
            },
            {
              title: statusNames[72],
              value: 52,
              statusTerm: 72,
              complete: null,
            },
          ];

        case 70: // Never Received - Vendor / RE
          return [
            {
              title: "Processed",
              value: 45,
              statusTerm: 70,
              complete: new Date().toISOString(),
            },
          ];

        case 71: // Warranty Discard
          return [
            {
              title: "Processed",
              value: 52,
              statusTerm: 71,
              complete: new Date().toISOString(),
            },
          ];

        case 72: // Never Received - Local Office
          return [
            {
              title: "Processed",
              value: 52,
              statusTerm: 72,
              complete: new Date().toISOString(),
            },
          ];

        case 68: // Pending - Manufacturer
          return [
            {
              title: "Pending Credit",
              value: 52,
              statusTerm: 79,
              complete: null,
            },
          ];

        case 79: // Pending Credit
          return [
            {
              title: "Processed",
              value: 52,
              statusTerm: 75,
              complete: new Date().toISOString(),
            },
            {
              title: "Denied",
              value: 52,
              statusTerm: 89,
              complete: null,
            },
          ];

        case 89: // Denied
          return [
            {
              title: "Processed",
              value: 52,
              statusTerm: 75,
              complete: new Date().toISOString(),
            },
          ];

        case 110: // Remove Sales Order
          return [
            {
              title: "Removed Sales Order",
              value: locationId,
              statusTerm: 73, // Received - Local Office
              complete: new Date().toISOString(),
            },
          ];

        default:
          return [];
      }
    };

    const handleUpdateServiceQuotePartChange = (updatedServiceQuotePart) => {
      this.setState((prevState) => {
        const updatedServiceQuoteParts = [
          ...prevState.updatedServiceQuoteParts,
        ];
        const updatedServiceQuotePartsFull = [...prevState.serviceQuoteParts];

        const isServiceQuotePartStatusTermId67Or68 =
          updatedServiceQuotePart.serviceQuotePartStatusTermId === 67 ||
          updatedServiceQuotePart.serviceQuotePartStatusTermId === 68;

        // Check if the original status was 89 (Denied)
        const originalPart = prevState.serviceQuoteParts.find(
          (part) => part.id === updatedServiceQuotePart.id,
        );

        const wasOriginallyDenied =
          originalPart?.serviceQuotePartStatusTermId === 89;

        const existingPartIndex = updatedServiceQuoteParts.findIndex(
          (part) => part.id === updatedServiceQuotePart.id,
        );

        if (existingPartIndex !== -1) {
          updatedServiceQuoteParts[existingPartIndex] = {
            ...updatedServiceQuoteParts[existingPartIndex],
            ...updatedServiceQuotePart,
          };
        } else {
          updatedServiceQuoteParts.push({
            ...updatedServiceQuotePart,
            wasOriginallyDenied: wasOriginallyDenied,
          });
        }

        const existingServicePartIndex = updatedServiceQuotePartsFull.findIndex(
          (part) => part.id === updatedServiceQuotePart.id,
        );

        if (existingServicePartIndex !== -1) {
          updatedServiceQuotePartsFull[existingServicePartIndex] = {
            ...updatedServiceQuotePartsFull[existingServicePartIndex],
            isServiceQuotePartStatusTermId67Or68:
              isServiceQuotePartStatusTermId67Or68,
            trackingNumber: updatedServiceQuotePart.trackingNumber,
            rmaNumber: updatedServiceQuotePart.rmaNumber,
            creditMemoNumber: updatedServiceQuotePart.creditMemoNumber,
            wasOriginallyDenied: wasOriginallyDenied,
          };
        }

        return {
          updatedServiceQuoteParts: updatedServiceQuoteParts,
          serviceQuoteParts: updatedServiceQuotePartsFull,
        };
      });
    };

    const getTrackingLink = (trackingNumber) => {
      const patterns = {
        UPS: /^1Z[0-9A-Z]{16}$/,
        FedEx: /^(96\d{20}|\d{15}|\d{12})$/,
        USPS: /^(94|93|92|95)[0-9]{20}$|^[A-Z]{2}[0-9]{9}US$/,
        DHL: /^\d{10}$/,
      };

      if (patterns.UPS.test(trackingNumber)) {
        return `https://www.ups.com/track?tracknum=${trackingNumber}`;
      } else if (patterns.FedEx.test(trackingNumber)) {
        return `https://www.fedex.com/fedextrack/?tracknumbers=${trackingNumber}`;
      } else if (patterns.USPS.test(trackingNumber)) {
        return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
      } else if (patterns.DHL.test(trackingNumber)) {
        return `https://www.dhl.com/en/express/tracking.html?AWB=${trackingNumber}`;
      } else {
        return null;
      }
    };

    return (
      <MUITableContainer component={MUIPaper} sx={{ mt: 2 }}>
        <MUITable stickyHeader size="small">
          <MUITableHead>
            <MUITableRow>
              <MUITableCell>Part Name</MUITableCell>
              <MUITableCell>Warranty</MUITableCell>
              <MUITableCell>Qty</MUITableCell>
              <MUITableCell>Status/Reason</MUITableCell>
              <MUITableCell>Route</MUITableCell>
            </MUITableRow>
          </MUITableHead>
          <MUITableBody>
            {this.state.serviceQuoteParts &&
            this.state.serviceQuoteParts.length > 0
              ? this.state.serviceQuoteParts.map((serviceQuotePart, idx) => {
                  const updatedPart = this.state.updatedServiceQuoteParts.find(
                    (updated) => updated.id === serviceQuotePart.id,
                  );

                  const mergedPart = {
                    ...serviceQuotePart,
                    ...updatedPart,
                  };

                  const isUpdatedServiceQuotePartStatusTermId75 = updatedPart
                    ? updatedPart.serviceQuotePartStatusTermId === 75
                    : null;
                  const isInitialServiceQuotePartStautsTermId75 =
                    serviceQuotePart.serviceQuotePartStatusTermId === 75;
                  const isUpdatedServiceQuotePartStatusTermId79 = updatedPart
                    ? updatedPart.serviceQuotePartStatusTermId === 79
                    : null;
                  const isInitialServiceQuotePartStautsTermId79 =
                    serviceQuotePart.serviceQuotePartStatusTermId === 79;
                  const isInitialServiceQuotePartStatusTermId67Or68 =
                    serviceQuotePart.serviceQuotePartStatusTermId === 67 ||
                    serviceQuotePart.serviceQuotePartStatusTermId === 68;
                  const isServiceQuotePartStatusTermId89 =
                    serviceQuotePart.serviceQuotePartStatusTermId === 89;

                  let isFieldInteractionDisabled = false;

                  if (serviceQuotePart.receivedAt !== null) {
                    isFieldInteractionDisabled = true;
                  } else if (
                    !setFilterMatches(
                      this.props.queue?.filter,
                      "service_quote_part.service_quote_part_status_term_id",
                      serviceQuotePart.serviceQuotePartStatusTermId,
                    )
                  ) {
                    isFieldInteractionDisabled = true;
                  }

                  const selectedOption = getRoutingOptions(
                    serviceQuotePart,
                    isFieldInteractionDisabled,
                  ).find(
                    (option) => option.value === serviceQuotePart.locationId,
                  );

                  const originalPart =
                    this.state.originalServiceQuoteParts.find(
                      (part) => part.id === serviceQuotePart.id,
                    );

                  return (
                    <React.Fragment key={idx}>
                      <MUITableRow
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <MUITableCell>
                          {serviceQuotePart.partName
                            ? serviceQuotePart.partName
                            : "N/A"}
                        </MUITableCell>
                        <MUITableCell>
                          {serviceQuotePart.warranty ? "Yes" : "No"}
                        </MUITableCell>
                        <MUITableCell>
                          {serviceQuotePart.quantity
                            ? serviceQuotePart.quantity
                            : null}
                        </MUITableCell>
                        <MUITableCell sx={{ width: "300px" }}>
                          {serviceQuotePart.receivedAt ? (
                            <MUITooltip
                              placement="left"
                              title={
                                serviceQuotePart.receivedByUserProfileId
                                  ? `Received at ${moment
                                      .utc(serviceQuotePart.receivedAt)
                                      .format("M/D/YYYY")} by ${
                                      this.context.usersIndexed[
                                        serviceQuotePart.receivedByUserProfileId
                                      ].firstname
                                    } ${
                                      this.context.usersIndexed[
                                        serviceQuotePart.receivedByUserProfileId
                                      ].lastname
                                    }`
                                  : `Received at ${moment
                                      .utc(serviceQuotePart.receivedAt)
                                      .format("M/D/YYYY")}`
                              }
                            >
                              <MUIBox
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                }}
                              >
                                {serviceQuotePart.serviceQuotePartStatusTermId ? (
                                  <span style={{ mr: 4 }}>
                                    <MUIBox component="span" sx={{ mr: 1 }}>
                                      {Term.getDisplayName(
                                        this.context.termsIndexed[
                                          serviceQuotePart
                                            .serviceQuotePartStatusTermId
                                        ],
                                      )}
                                      {serviceQuotePart.serviceQuotePartReasonTermId ? (
                                        <MUIBox component="div">
                                          {Term.getDisplayName(
                                            this.context.termsIndexed[
                                              serviceQuotePart
                                                .serviceQuotePartReasonTermId
                                            ],
                                          )}
                                        </MUIBox>
                                      ) : null}
                                    </MUIBox>
                                  </span>
                                ) : null}
                                <MUICheckIcon
                                  fontSize="small"
                                  sx={{
                                    color: (theme) =>
                                      theme.palette.success.light,
                                  }}
                                />
                              </MUIBox>
                            </MUITooltip>
                          ) : serviceQuotePart.serviceQuotePartStatusTermId ? (
                            <>
                              {Term.getDisplayName(
                                this.context.termsIndexed[
                                  serviceQuotePart.serviceQuotePartStatusTermId
                                ],
                              )}
                              {serviceQuotePart.serviceQuotePartReasonTermId ? (
                                <MUIBox component="div">
                                  {Term.getDisplayName(
                                    this.context.termsIndexed[
                                      serviceQuotePart
                                        .serviceQuotePartReasonTermId
                                    ],
                                  )}
                                </MUIBox>
                              ) : null}
                            </>
                          ) : null}
                        </MUITableCell>
                        <MUITableCell sx={{ width: "300px" }}>
                          <MUIAutocomplete
                            disabled={isFieldInteractionDisabled}
                            options={getRoutingOptions(
                              serviceQuotePart,
                              isFieldInteractionDisabled,
                            )}
                            isOptionEqualToValue={(option, value) =>
                              option.title === value.title
                            }
                            getOptionLabel={(option) => option.title}
                            defaultValue={
                              isFieldInteractionDisabled ? selectedOption : null
                            }
                            onChange={(e, newValue) => {
                              const updatedTrackingNumber =
                                newValue &&
                                (newValue.statusTerm === 67 ||
                                  newValue.statusTerm === 68)
                                  ? serviceQuotePart.trackingNumber
                                  : originalPart.trackingNumber;

                              const updatedRmaNumber =
                                newValue &&
                                (newValue.statusTerm === 67 ||
                                  newValue.statusTerm === 68)
                                  ? serviceQuotePart.rmaNumber
                                  : originalPart.rmaNumber;

                              handleUpdateServiceQuotePartChange({
                                ...serviceQuotePart,
                                locationId: newValue ? newValue.value : null,
                                serviceQuotePartStatusTermId: newValue
                                  ? newValue.statusTerm
                                  : null,
                                receivedAt: newValue ? newValue.complete : null,
                                followupDate: new Date().toISOString(),
                                trackingNumber: updatedTrackingNumber,
                                rmaNumber: updatedRmaNumber,
                              });

                              this.setState((prevState) => ({
                                serviceQuoteParts:
                                  prevState.serviceQuoteParts.map((part) =>
                                    part.id === serviceQuotePart.id
                                      ? {
                                          ...part,
                                          trackingNumber: updatedTrackingNumber,
                                          rmaNumber: updatedRmaNumber,
                                        }
                                      : part,
                                  ),
                              }));
                            }}
                            renderInput={(params) => {
                              return (
                                <MUITextField
                                  {...params}
                                  size="small"
                                  variant="standard"
                                  fullWidth={true}
                                />
                              );
                            }}
                          />
                        </MUITableCell>
                      </MUITableRow>

                      {/* Additional Fields for RMA # and Tracking # */}
                      {serviceQuotePart.isServiceQuotePartStatusTermId67Or68 ||
                      isInitialServiceQuotePartStautsTermId79 ||
                      (isInitialServiceQuotePartStatusTermId67Or68 &&
                        (serviceQuotePart.rmaNumber ||
                          serviceQuotePart.trackingNumber)) ||
                      (isFieldInteractionDisabled &&
                        (serviceQuotePart.rmaNumber ||
                          serviceQuotePart.trackingNumber)) ||
                      isUpdatedServiceQuotePartStatusTermId79 ||
                      isUpdatedServiceQuotePartStatusTermId75 ? (
                        <MUITableRow
                          sx={{
                            backgroundColor: (theme) =>
                              theme.palette.background.paper,
                          }}
                        >
                          <MUITableCell></MUITableCell>
                          <MUITableCell
                            colSpan={2}
                            sx={{ pt: "4px", pb: "4px", width: "200px" }}
                          >
                            {isUpdatedServiceQuotePartStatusTermId75 ||
                            isInitialServiceQuotePartStautsTermId75 ||
                            isServiceQuotePartStatusTermId89 ? (
                              <MUITextField
                                label="Credit Memo #"
                                autoFocus={true}
                                defaultValue={
                                  serviceQuotePart.creditMemoNumber || ""
                                }
                                onChange={(e) => {
                                  handleUpdateServiceQuotePartChange({
                                    ...mergedPart,
                                    creditMemoNumber: e.target.value.trim(),
                                  });
                                }}
                                size="small"
                                variant="standard"
                                fullWidth
                                required={
                                  !isFieldInteractionDisabled &&
                                  !isServiceQuotePartStatusTermId89
                                }
                                disabled={
                                  isFieldInteractionDisabled ||
                                  isInitialServiceQuotePartStautsTermId75
                                }
                                sx={{
                                  margin: 0,
                                  "& .MuiInputBase-root": {
                                    fontSize: "0.875rem",
                                  },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "0.75rem",
                                  },
                                }}
                              />
                            ) : null}
                          </MUITableCell>
                          <MUITableCell sx={{ pt: "4px", pb: "4px" }}>
                            <MUITextField
                              label="RMA #"
                              autoFocus={true}
                              value={
                                mergedPart.serviceQuotePartStatusTermId ===
                                  67 ||
                                mergedPart.serviceQuotePartStatusTermId === 68
                                  ? mergedPart.rmaNumber || ""
                                  : originalPart?.rmaNumber || ""
                              }
                              onChange={(e) =>
                                handleUpdateServiceQuotePartChange({
                                  ...mergedPart,
                                  rmaNumber: e.target.value,
                                })
                              }
                              size="small"
                              variant="standard"
                              fullWidth
                              required={
                                isFieldInteractionDisabled ||
                                !serviceQuotePart.isServiceQuotePartStatusTermId67Or68
                                  ? false
                                  : true
                              }
                              disabled={
                                isFieldInteractionDisabled ||
                                !serviceQuotePart.isServiceQuotePartStatusTermId67Or68
                              }
                              sx={{
                                margin: 0,
                                "& .MuiInputBase-root": {
                                  fontSize: "0.875rem",
                                },
                                "& .MuiInputLabel-root": {
                                  fontSize: "0.75rem",
                                },
                              }}
                            />
                          </MUITableCell>
                          <MUITableCell sx={{ pt: "4px", pb: "4px" }}>
                            <MUITextField
                              label="Tracking #"
                              value={
                                mergedPart.serviceQuotePartStatusTermId ===
                                  67 ||
                                mergedPart.serviceQuotePartStatusTermId === 68
                                  ? mergedPart.trackingNumber || ""
                                  : originalPart?.trackingNumber || ""
                              }
                              onChange={(e) => {
                                handleUpdateServiceQuotePartChange({
                                  ...mergedPart,
                                  trackingNumber: e.target.value,
                                });
                              }}
                              size="small"
                              variant="standard"
                              fullWidth
                              required={
                                isFieldInteractionDisabled ||
                                !serviceQuotePart.isServiceQuotePartStatusTermId67Or68
                                  ? false
                                  : true
                              }
                              disabled={
                                isFieldInteractionDisabled ||
                                !serviceQuotePart.isServiceQuotePartStatusTermId67Or68
                              }
                              sx={{
                                margin: 0,
                                "& .MuiInputBase-root": {
                                  fontSize: "0.875rem",
                                },
                                "& .MuiInputLabel-root": {
                                  fontSize: "0.75rem",
                                },
                              }}
                              slotProps={{
                                input: {
                                  endAdornment: (() => {
                                    const trackingUrl = getTrackingLink(
                                      serviceQuotePart.trackingNumber,
                                    );
                                    return trackingUrl ? (
                                      <MUITooltip title="Track Shipment">
                                        <MUIButton
                                          onClick={() => {
                                            window.open(trackingUrl, "_blank");
                                          }}
                                          sx={{
                                            minWidth: 0,
                                            padding: 0,
                                            color: (theme) =>
                                              theme.palette.success.light,
                                          }}
                                        >
                                          <MUILocalShippingIcon fontSize="small" />
                                        </MUIButton>
                                      </MUITooltip>
                                    ) : null;
                                  })(),
                                },
                              }}
                            />
                          </MUITableCell>
                        </MUITableRow>
                      ) : null}
                    </React.Fragment>
                  );
                })
              : null}
          </MUITableBody>
        </MUITable>
      </MUITableContainer>
    );
  }

  /**
   * Render cancel and confirm button actions of dialog.
   */
  renderActions() {
    const isUpdateButtonEnabled = () => {
      if (
        !this.state.updatedServiceQuoteParts ||
        this.state.updatedServiceQuoteParts === 0 ||
        !this.state.serviceQuoteParts ||
        this.state.serviceQuoteParts === 0
      ) {
        return false;
      }

      const eligiblePartsForUpdate = this.state.serviceQuoteParts.filter(
        (part) =>
          part.receivedAt === null &&
          setFilterMatches(
            this.props.queue?.filter,
            "service_quote_part.service_quote_part_status_term_id",
            part.serviceQuotePartStatusTermId,
          ),
      );

      const eligiblePartsHaveUpdateMatch =
        eligiblePartsForUpdate.length > 0 &&
        eligiblePartsForUpdate.every((part) => {
          const updatedPart = this.state.updatedServiceQuoteParts.find(
            (updated) => updated.id === part.id,
          );

          if (!updatedPart || updatedPart.locationId == null) return false;

          // Check for serviceQuotePartStatusTermId is Pending - Rehab Express || Pending - Manufacturer
          if ([67, 68].includes(updatedPart.serviceQuotePartStatusTermId)) {
            if (!updatedPart.rmaNumber || !updatedPart.trackingNumber) {
              return false;
            }
          }

          // Check for status serviceQuotePartStatusTermId is Pending Credit
          if (
            updatedPart.serviceQuotePartStatusTermId === 75 &&
            !updatedPart.wasOriginallyDenied
          ) {
            if (!updatedPart.creditMemoNumber) {
              return false;
            }
          }

          return true;
        });

      return !eligiblePartsHaveUpdateMatch;
    };

    return (
      <MUIDialogActions spacing={2}>
        <MUIButton variant="text" onClick={this.props.onClose}>
          Cancel
        </MUIButton>
        <MUIButton
          variant="contained"
          disabled={isUpdateButtonEnabled()}
          loading={this.props.loading}
          onClick={() =>
            this.props.onConfirm(this.state.updatedServiceQuoteParts)
          }
        >
          Confirm
        </MUIButton>
      </MUIDialogActions>
    );
  }

  /**
   * @returns {string} The max width of the dialog.
   */
  getMaxWidth() {
    return "lg";
  }
}
